/* ─────────────────────────────────────────────────────────────────────────
   warranty-registration.js
   ─────────────────────────────────────────────────────────────────────────
   Handles the Warranty Registration form on the Shopify storefront.

   Flow:
     1. Read serial + token from URL query params (set by the email CTA).
     2. Pre-fill the form fields.
     3. On submit, validate locally, then POST to the Shopify App Proxy
        (/apps/shopify-proxy) which forwards to the Odoo controller.
     4. On success, hide the form and show the prominent success block
        defined in the Liquid template (#warranty-form-success).

   Set DEBUG = true temporarily when troubleshooting locally.
   ───────────────────────────────────────────────────────────────────────── */

const DEBUG = false;

document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('warranty-registration-form');
  if (!form) return;

  const serialInput = document.getElementById('wr-serial');
  const tokenInput = document.getElementById('wr-token');
  const emailInput = document.getElementById('wr-email');
  const countryInput = document.getElementById('wr-country');
  const statusBox = document.getElementById('warranty-form-status');
  const successBlock = document.getElementById('warranty-form-success');
  const submitButton = form.querySelector('button[type="submit"]');

  const ENDPOINT = '/apps/shopify-proxy';

  if (DEBUG) console.log('warranty-registration.js loaded — form found');

  // ─── Helpers ────────────────────────────────────────────────────────────

  function getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  function setStatus(message, type = '') {
    if (!statusBox) return;
    statusBox.textContent = message || '';
    statusBox.classList.remove('is-success', 'is-error', 'is-loading');
    if (type) {
      statusBox.classList.add(type);
    }
  }

  function clearErrors() {
    form.querySelectorAll('[data-error-for]').forEach((el) => {
      el.textContent = '';
    });
  }

  function setFieldError(fieldName, message) {
    const errorEl = form.querySelector(`[data-error-for="${fieldName}"]`);
    if (errorEl) {
      errorEl.textContent = message;
    }
  }

  function getCheckedRadioValue(name) {
    const checked = form.querySelector(`input[name="${name}"]:checked`);
    return checked ? checked.value : null;
  }

  // ─── Validation ─────────────────────────────────────────────────────────

  function validateForm() {
    clearErrors();
    let isValid = true;

    const email = emailInput?.value.trim() || '';
    const country = countryInput?.value || '';
    const serial = serialInput?.value.trim() || '';
    const token = tokenInput?.value.trim() || '';
    const satisfaction = getCheckedRadioValue('overall_satisfaction');
    const marketingConsent = getCheckedRadioValue('marketing_consent');
    const tosChecked = form.querySelector('input[name="tos"]')?.checked || false;

    // Email
    if (!email) {
      setFieldError('email', 'Email is required.');
      isValid = false;
    } else {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailOk) {
        setFieldError('email', 'Please enter a valid email address.');
        isValid = false;
      }
    }

    // Country
    if (!country) {
      setFieldError('country', 'Country is required.');
      isValid = false;
    }

    // Serial number
    if (!serial) {
      setFieldError('serial_number', 'Serial number is required.');
      isValid = false;
    }

    // Token (link integrity) — shown as form-level error, not on serial field
    if (!token) {
      setStatus(
        'This registration link looks invalid. Please use the link from the email we sent you, or contact support.',
        'is-error'
      );
      isValid = false;
    }

    // Satisfaction (stars)
    if (!satisfaction) {
      setFieldError('overall_satisfaction', 'Please rate your experience.');
      isValid = false;
    }

    // Marketing consent
    if (!marketingConsent) {
      setFieldError('marketing_consent', 'Please choose an option.');
      isValid = false;
    }

    // Terms of service
    if (!tosChecked) {
      setFieldError('tos', 'You must accept the terms and privacy policy.');
      isValid = false;
    }

    return isValid;
  }

  // ─── Payload construction ───────────────────────────────────────────────

  function buildPayload() {
    const overall_satisfactionRaw = getCheckedRadioValue('overall_satisfaction');
    const marketingConsentRaw = getCheckedRadioValue('marketing_consent');

    return {
      action: 'register_warranty',
      token: tokenInput?.value.trim() || '',
      serial_number: serialInput?.value.trim() || '',
      email: emailInput?.value.trim() || '',
      country: countryInput?.value || '',
      overall_satisfaction: overall_satisfactionRaw ? parseInt(overall_satisfactionRaw, 10) : null,
      marketing_consent: marketingConsentRaw === 'yes',
      tos: form.querySelector('input[name="tos"]')?.checked || false,
      shop_locale: document.documentElement.lang || '',
      page_url: window.location.href
    };
  }

  // ─── Network ────────────────────────────────────────────────────────────

  async function submitWarranty(payload) {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });

    let data = {};
    try {
      data = await response.json();
    } catch (err) {
      throw new Error('Invalid server response.');
    }

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Unable to submit warranty registration.');
    }

    return data;
  }

  // ─── URL prefill ────────────────────────────────────────────────────────

  function prefillFromUrl() {
    const serial = getQueryParam('serial');
    const token = getQueryParam('token');
    const email = getQueryParam('email');
    const country = getQueryParam('country');

    if (serial && serialInput) {
      serialInput.value = serial;
    }

    if (token && tokenInput) {
      tokenInput.value = token;
    }

    if (email && emailInput && !emailInput.value) {
      emailInput.value = email;
    }

    if (country && countryInput && !countryInput.value) {
      countryInput.value = country.toUpperCase();
    }
  }

  // ─── Submit handler ─────────────────────────────────────────────────────

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      // Only show the generic "review fields" message if we don't already
      // have a form-level error from validateForm() (e.g., invalid token).
      if (!statusBox.classList.contains('is-error')) {
        setStatus('Please review the highlighted fields.', 'is-error');
      }
      return;
    }

    const payload = buildPayload();
    if (DEBUG) console.log('Warranty payload:', payload);

    try {
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.dataset.originalText = submitButton.textContent;
        submitButton.textContent = 'Sending...';
      }

      setStatus('Sending your warranty registration...', 'is-loading');

      await submitWarranty(payload);

      // ─── Success ───
      clearErrors();
      setStatus('');

      // Show the prominent success block defined in the Liquid template
      if (successBlock) {
        successBlock.removeAttribute('hidden');
        successBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Hide the form so the user doesn't try to submit again
      form.querySelectorAll('.wr-section, .wr-submit, .wr-form__note').forEach((el) => {
        el.style.display = 'none';
      });

    } catch (error) {
      setStatus(error.message || 'An error occurred while submitting the form.', 'is-error');

      if (submitButton && submitButton.dataset.originalText) {
        submitButton.textContent = submitButton.dataset.originalText;
      }
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  });

  // ─── Init ───────────────────────────────────────────────────────────────

  prefillFromUrl();
});